import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Building2, Key, ArrowRight, CheckCircle, AlertCircle, ArrowLeft, User, Mail, Lock, Phone } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ClubInstall = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  
  const [step, setStep] = useState(1); // 1: Enter licence, 2: Create admin
  const [licenceCode, setLicenceCode] = useState('');
  const [clubInfo, setClubInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  const [adminForm, setAdminForm] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    nom: '',
    prenom: '',
    telephone: ''
  });

  // Format licence code as user types (XXXX-XXXX-XXXX-XXXX)
  const formatLicenceCode = (value) => {
    // Remove all non-alphanumeric characters
    const cleaned = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
    // Split into groups of 4
    const groups = cleaned.match(/.{1,4}/g) || [];
    return groups.slice(0, 4).join('-');
  };

  const handleLicenceChange = (e) => {
    const formatted = formatLicenceCode(e.target.value);
    setLicenceCode(formatted);
    setError('');
  };

  const handleCheckLicence = async () => {
    if (licenceCode.length !== 19) { // XXXX-XXXX-XXXX-XXXX = 19 chars
      setError('Le code de licence doit contenir 16 caractères');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.get(`${API_URL}/api/clubs/check-licence/${licenceCode}`);
      if (response.data.valid) {
        setClubInfo(response.data);
        setStep(2);
      } else {
        setError(response.data.licence_message || 'Licence invalide');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Code de licence invalide');
    } finally {
      setLoading(false);
    }
  };

  const handleInstall = async () => {
    // Validation
    if (!adminForm.email || !adminForm.password || !adminForm.nom || !adminForm.prenom) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    if (adminForm.password !== adminForm.confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (adminForm.password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/clubs/install`, {
        licence_code: licenceCode,
        email: adminForm.email,
        password: adminForm.password,
        nom: adminForm.nom,
        prenom: adminForm.prenom,
        telephone: adminForm.telephone || null
      });
      
      // Login the new admin
      login(response.data.token, response.data.user);
      
      toast.success(`Installation réussie ! Bienvenue sur ${response.data.club.nom}`);
      
      // Redirect to dashboard
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'installation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F5F0E8] to-white flex flex-col">
      {/* Header */}
      <header className="p-4">
        <Link to="/" className="flex items-center gap-2 text-[#2D5A3D] hover:opacity-80">
          <ArrowLeft className="w-5 h-5" />
          <span>Retour à l&apos;accueil</span>
        </Link>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 mb-4">
              <Building2 className="w-10 h-10 text-[#2D5A3D]" />
              <h1 className="text-3xl font-bold text-gray-900">Equibook</h1>
            </div>
            <p className="text-gray-600">Installation de votre club</p>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <div className={`flex items-center gap-2 ${step >= 1 ? 'text-[#2D5A3D]' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 1 ? 'bg-[#2D5A3D] text-white' : 'bg-gray-200'}`}>
                {step > 1 ? <CheckCircle className="w-5 h-5" /> : '1'}
              </div>
              <span className="hidden sm:inline text-sm">Licence</span>
            </div>
            <div className="w-8 h-[2px] bg-gray-200">
              <div className={`h-full transition-all ${step >= 2 ? 'bg-[#2D5A3D] w-full' : 'w-0'}`} />
            </div>
            <div className={`flex items-center gap-2 ${step >= 2 ? 'text-[#2D5A3D]' : 'text-gray-400'}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${step >= 2 ? 'bg-[#2D5A3D] text-white' : 'bg-gray-200'}`}>
                2
              </div>
              <span className="hidden sm:inline text-sm">Administrateur</span>
            </div>
          </div>

          {/* Step 1: Enter Licence Code */}
          {step === 1 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5 text-[#2D5A3D]" />
                  Code de licence
                </CardTitle>
                <CardDescription>
                  Entrez le code de licence fourni par l&apos;administrateur Equibook
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid gap-2">
                    <Label htmlFor="licence">Code de licence</Label>
                    <Input
                      id="licence"
                      value={licenceCode}
                      onChange={handleLicenceChange}
                      placeholder="XXXX-XXXX-XXXX-XXXX"
                      className="text-center text-lg font-mono tracking-wider"
                      maxLength={19}
                    />
                  </div>
                  
                  {error && (
                    <div className="flex items-center gap-2 text-red-600 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {error}
                    </div>
                  )}
                  
                  <Button
                    onClick={handleCheckLicence}
                    disabled={loading || licenceCode.length !== 19}
                    className="w-full bg-[#2D5A3D] hover:bg-[#234830]"
                  >
                    {loading ? 'Vérification...' : 'Vérifier la licence'}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Step 2: Create Admin Account */}
          {step === 2 && clubInfo && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-[#2D5A3D]" />
                  Créer votre compte administrateur
                </CardTitle>
                <CardDescription>
                  <div className="mt-2 p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="font-medium text-green-800">{clubInfo.club_nom}</p>
                    {clubInfo.club_ville && (
                      <p className="text-sm text-green-600">{clubInfo.club_adresse}, {clubInfo.club_ville}</p>
                    )}
                  </div>
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="grid gap-2">
                      <Label htmlFor="prenom">Prénom *</Label>
                      <Input
                        id="prenom"
                        value={adminForm.prenom}
                        onChange={(e) => setAdminForm({...adminForm, prenom: e.target.value})}
                        placeholder="Jean"
                      />
                    </div>
                    <div className="grid gap-2">
                      <Label htmlFor="nom">Nom *</Label>
                      <Input
                        id="nom"
                        value={adminForm.nom}
                        onChange={(e) => setAdminForm({...adminForm, nom: e.target.value})}
                        placeholder="DUPONT"
                      />
                    </div>
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="email">Email *</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        id="email"
                        type="email"
                        value={adminForm.email}
                        onChange={(e) => setAdminForm({...adminForm, email: e.target.value})}
                        placeholder="admin@club.fr"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="telephone">Téléphone</Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        id="telephone"
                        value={adminForm.telephone}
                        onChange={(e) => setAdminForm({...adminForm, telephone: e.target.value})}
                        placeholder="06 12 34 56 78"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="password">Mot de passe *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        id="password"
                        type="password"
                        value={adminForm.password}
                        onChange={(e) => setAdminForm({...adminForm, password: e.target.value})}
                        placeholder="Minimum 8 caractères"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  <div className="grid gap-2">
                    <Label htmlFor="confirmPassword">Confirmer le mot de passe *</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        id="confirmPassword"
                        type="password"
                        value={adminForm.confirmPassword}
                        onChange={(e) => setAdminForm({...adminForm, confirmPassword: e.target.value})}
                        placeholder="Confirmez votre mot de passe"
                        className="pl-10"
                      />
                    </div>
                  </div>
                  
                  {error && (
                    <div className="flex items-center gap-2 text-red-600 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      {error}
                    </div>
                  )}
                  
                  <div className="flex gap-3">
                    <Button
                      variant="outline"
                      onClick={() => { setStep(1); setError(''); }}
                      className="flex-1"
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Retour
                    </Button>
                    <Button
                      onClick={handleInstall}
                      disabled={loading}
                      className="flex-1 bg-[#2D5A3D] hover:bg-[#234830]"
                    >
                      {loading ? 'Installation...' : 'Installer le club'}
                      <CheckCircle className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Help Text */}
          <p className="text-center text-sm text-gray-500 mt-6">
            Vous n&apos;avez pas de code de licence ?{' '}
            <a href="mailto:contact@equibook.fr" className="text-[#2D5A3D] hover:underline">
              Contactez-nous
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};
